import { IBookingSearchResult } from "../../../Orchestrator/Types/Index";
import { AdmissionStatusEnum, BookingPartTypeEnum } from "../../../Orchestrator/Types/Enums/globalDefaultEnum";

/**
 * Helpers for the bookings list's lazy pagination (both tabs).
 *
 * The custom search returns every booking's lightweight summary in one call, so
 * we sort the summaries by date up front (`sortBookingSummariesForDisplay`) but
 * build the expensive per-booking details only a page at a time. "Load more"
 * builds the next page and appends it, in the pre-sorted order — so rows already
 * on screen never reshuffle. `hasMoreToShow` reports whether unbuilt summaries
 * remain (offset = summaries built so far, total = `allResults.length`).
 */

/**
 * Earliest trip start of a booking summary — the date a built card exposes as
 * its `departureDateTime` (outbound/primary leg). Summaries with no usable date
 * sort to the end of either list.
 *
 * Note: for an exchanged/changed booking the summary date can differ from the
 * rebuilt card date — an accepted tradeoff of not building every detail up front.
 */
function summaryTripTime(booking: IBookingSearchResult, activeTab: "1" | "2"): number {
    const times = (booking.tripSummaries || [])
        .map((trip) => new Date(trip.startTime).getTime())
        .filter((time) => !Number.isNaN(time));
    if (times.length === 0) {
        return activeTab === "1" ? Number.POSITIVE_INFINITY : Number.NEGATIVE_INFINITY;
    }
    return Math.min(...times);
}

/**
 * Whether a booking is entirely cancelled — the state the Upcoming sort sinks.
 * True when, of the admissions not already exchanged away, at least one
 * remains and every one is REFUNDED or RELEASED.
 *
 * Must agree with the badge, which `resolveOfferStatus` derives from
 * `offer.admissions` alone. Hence the two filters:
 *
 *  - ADMISSIONS ONLY, because the badge derives from `offer.admissions` alone.
 *    `bookingPartSummaries` also carries Ancillary and Reservation parts, each
 *    with its own status field (live INT payloads on 2026-08-03 show all three
 *    types on TB43JM), so counting them could reject a booking the card calls
 *    "cancelled". `determineBookingStatusMeta` filters to admissions for the
 *    same reason — keep the two in step.
 *  - EXCHANGED parts dropped at PART level, not offer level.
 *    `GetAllOfferWithoutExchangeAction`, which feeds the card builder, drops
 *    individual EXCHANGED admissions and keeps the offer when any survive — so
 *    an offer can hold one exchanged-away passenger next to one still
 *    travelling. Dropping the whole offer would bury a confirmed trip.
 *
 * REFUNDED and RELEASED are one state here because `resolveOfferStatus` maps
 * an all-REFUNDED-or-RELEASED admission set to "REFUNDED".
 *
 * Also consumed by `determineBookingStatusMeta` (`UpcomingPastBookingTabAction`),
 * which drops multi-trip bookings whose derived status is neither FULFILLED
 * nor REFUNDED — on BOTH tabs. Editing this changes multi-trip list
 * membership, not just sort order.
 *
 * Known limitation: this is a BOOKING-level answer while badges are per card.
 * Cancel ONE PASSENGER of a multi-passenger booking and the builder emits both
 * a "Cancelled" and a "Confirmed" card; this returns false, so neither is
 * sunk. (Not one leg of a round trip — a same-period round trip builds a
 * single card from the combined admissions.) Within a page
 * `GetBookingToRightDataAction` still sorts built cards FULFILLED-first, so
 * the mis-ordering only shows across page boundaries — a cancelled card on
 * page 1 sitting above an active one revealed by "Load more". Closing it
 * needs ordering applied to the accumulated card list, not per page.
 */
export function isFullyCancelledBooking(booking: IBookingSearchResult): boolean {
    const liveParts = (booking.bookedOfferSummaries || [])
        .flatMap((summary) => summary.bookingPartSummaries || [])
        .filter(
            (part) =>
                part.type === BookingPartTypeEnum.ADMISSION &&
                part.status !== AdmissionStatusEnum.EXCHANGED
        );
    return (
        liveParts.length > 0 &&
        liveParts.every(
            (part) =>
                part.status === AdmissionStatusEnum.REFUNDED ||
                part.status === AdmissionStatusEnum.RELEASED
        )
    );
}

/**
 * Sorts booking summaries into display order: Upcoming (tab "1") soonest-first
 * (ascending), Past (tab "2") most-recent-first (descending). Returns a new
 * array; the input is not mutated.
 *
 * On the Upcoming tab only, fully-cancelled (REFUNDED) bookings sink below every
 * active booking, keeping their own date order within that group. Past is left
 * in pure date order so a cancelled trip stays findable by its date instead of
 * being buried beneath a long history of active bookings.
 */
export function sortBookingSummariesForDisplay(
    bookings: IBookingSearchResult[],
    activeTab: "1" | "2"
): IBookingSearchResult[] {
    const direction = activeTab === "1" ? 1 : -1;
    const sinkCancelled = activeTab === "1";
    return [...bookings].sort((a, b) => {
        if (sinkCancelled) {
            const aCancelled = isFullyCancelledBooking(a) ? 1 : 0;
            const bCancelled = isFullyCancelledBooking(b) ? 1 : 0;
            if (aCancelled !== bCancelled) {
                // Cancelled (1) always sorts after active (0).
                return aCancelled - bCancelled;
            }
        }
        return direction * (summaryTripTime(a, activeTab) - summaryTripTime(b, activeTab));
    });
}

/**
 * Whether "Load more" should still show: more summaries remain to be built.
 * `offset` is how many summaries have been built so far (null/undefined => none
 * built yet, treated as "nothing more to reveal").
 */
export function hasMoreToShow(
    total: number,
    offset: number | null | undefined
): boolean {
    if (offset == null) {
        return false;
    }
    return total > offset;
}
